/** The row of buttons in a multiple choice challenge */
import * as preact from "preact";
import * as _ from "underscore";

import * as keyboardShortcuts from "keyboardShortcuts";
import UserWordModel from "models/UserWordModel";
import * as transliterations from "transliterations";
import TransliterationView from "views/TransliterationView";

interface Props {
  correctAnswer: string;
  distractors: string[];
  onSuccess: () => void;
  onFail?: () => void;
  showCorrectAnswer: boolean;
  transliterationLanguage?: string;
}

interface State {
  selectedIndex?: number;
  shuffledOptions: {
    text: string;
    correct: boolean;
  }[];
  showTransliterations?: boolean;
}

class MultipleChoiceButtonRow extends preact.Component<Props, State> {
  userWords: Backbone.Collection<UserWordModel>;
  buttonElements: HTMLButtonElement[] = [];

  constructor(props: Props) {
    super(props);

    this.state = {
      shuffledOptions: _.shuffle([
        {
          text: props.correctAnswer,
          correct: true,
        },
        ...props.distractors.map((option) => ({
          text: option,
          correct: false,
        })),
      ]),
      selectedIndex: undefined,
    };

    if (this.props.transliterationLanguage) {
      transliterations
        .show(this.props.transliterationLanguage)
        .then((showTransliterations) => {
          this.setState({ showTransliterations });
        });
    }
  }

  render() {
    return (
      <div class="multipleChoiceButtonRow">
        {this.state.shuffledOptions.map((option, index) => (
          <button
            ref={(element) => (this.buttonElements[index] = element)}
            onClick={() => this.choose(index)}
            class={
              this.props.showCorrectAnswer
                ? this.state.shuffledOptions[index].correct
                  ? "correct"
                  : ""
                : this.state.selectedIndex !== undefined
                  ? this.state.shuffledOptions[index].correct
                    ? "correct"
                    : index === this.state.selectedIndex
                      ? "incorrect"
                      : ""
                  : ""
            }
            disabled={
              this.props.showCorrectAnswer ||
              this.state.selectedIndex !== undefined
            }
          >
            <small>[{index + 1}]</small>{" "}
            {this.props.transliterationLanguage &&
            this.state.showTransliterations ? (
              <span class="transliterationContainer">
                <TransliterationView
                  language={this.props.transliterationLanguage}
                  text={option.text}
                />
                <div>{option.text}</div>
              </span>
            ) : (
              option.text
            )}
          </button>
        ))}
      </div>
    );
  }

  componentDidMount() {
    for (const buttonIndex of _.range(this.state.shuffledOptions.length)) {
      keyboardShortcuts.registerHandler(
        "practicePage",
        `${buttonIndex + 1}`.charCodeAt(0),
        () => {
          if (
            this.state.selectedIndex !== undefined ||
            this.props.showCorrectAnswer
          ) {
            // Do nothing in these cases
            return;
          }

          // Click button
          if (this.buttonElements[buttonIndex]) {
            this.buttonElements[buttonIndex].click();
          }
        }
      );
    }
  }

  componentWillUnmount(): void {
    for (const index of _.range(this.state.shuffledOptions.length)) {
      keyboardShortcuts.unregisterHandler(
        "practicePage",
        `${index + 1}`.charCodeAt(0)
      );
    }
  }

  choose(index: number) {
    if (this.state.shuffledOptions[index].correct) {
      this.props.onSuccess();
    } else {
      this.props.onFail?.();
    }

    this.setState({ selectedIndex: index });
  }
}

export default MultipleChoiceButtonRow;
